import type { AuthorizationSnapshot } from '@delta/domain';

export interface AuthStorage<TUser> {
  getToken: () => string | null;
  setToken: (token: string) => void;
  getUser: () => TUser | null;
  setUser: (user: TUser) => void;
  clear: () => void;
}

export interface AuthStorageKeys {
  token: string;
  user: string;
}

export interface StoredAuthSession<TUser> {
  token: string;
  user: TUser;
}

export function createJsonAuthStorage<TUser>(
  storage: Storage,
  keys: AuthStorageKeys,
): AuthStorage<TUser> {
  return {
    getToken: () => storage.getItem(keys.token),
    setToken: (token) => storage.setItem(keys.token, token),
    getUser: () => {
      const value = storage.getItem(keys.user);
      if (!value) return null;
      try {
        return JSON.parse(value) as TUser;
      } catch {
        storage.removeItem(keys.user);
        return null;
      }
    },
    setUser: (user) => storage.setItem(keys.user, JSON.stringify(user)),
    clear: () => {
      storage.removeItem(keys.token);
      storage.removeItem(keys.user);
    },
  };
}

export function readStoredAuthSession<TUser>(
  authStorage: AuthStorage<TUser>,
): StoredAuthSession<TUser> | null {
  const token = authStorage.getToken();
  const user = authStorage.getUser();
  if (token && user) return { token, user };
  if (token || user) authStorage.clear();
  return null;
}

export function hasPermission(
  snapshot: Pick<AuthorizationSnapshot, 'permissions'> | null | undefined,
  permissionCode: string,
) {
  return snapshot?.permissions.includes('*') === true
    || snapshot?.permissions.includes(permissionCode) === true;
}

export function hasRole(
  snapshot: Pick<AuthorizationSnapshot, 'roles'> | null | undefined,
  roleCode: string,
) {
  return snapshot?.roles.includes(roleCode) === true;
}
